import { initializeApp } from 'firebase/app';
import { getAnalytics, logEvent, Analytics } from 'firebase/analytics';
import { getFirestore, collection, addDoc, Timestamp } from 'firebase/firestore';

// Firebase configuration with fallback values
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyB-ixDxLbBFG4IczqyE-0TOFi8uVFj1OM8',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'kode-app.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'kode-app',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'kode-app.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '50100873998',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:50100873998:web:ed40497958d1828bdd855f',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-390Y9J2SW3'
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Analytics (only in browser)
let analytics: Analytics | null = null;
if (typeof window !== 'undefined') {
  analytics = getAnalytics(app);
}

// Initialize Firestore
const db = getFirestore(app);

// Track custom events
export const trackEvent = (eventName: string, params?: Record<string, string | number | boolean>) => {
  if (analytics) {
    logEvent(analytics, eventName, params);
  }
};

// MVP Build Page specific tracking events
export const trackMVPBuild = {
  // CTA clicks
  whatsappClick: () => trackEvent('mvp_cta_click', { cta_type: 'whatsapp', page: 'mvp_build' }),
  bookCallClick: () => trackEvent('mvp_cta_click', { cta_type: 'book_call', page: 'mvp_build' }),

  // Methodology modal
  methodologyOpen: () => trackEvent('mvp_methodology_view', { action: 'open' }),
  methodologyClose: () => trackEvent('mvp_methodology_view', { action: 'close' }),

  // Section views (scroll tracking)
  sectionView: (section: string) => trackEvent('mvp_section_view', { section, page: 'mvp_build' }),

  // Page view
  pageView: () => trackEvent('page_view', { page_title: 'MVP Build', page_path: '/mvp-build' }),
};

// Grant Application Types
export interface GrantApplication {
  // Personal Info
  fullName: string;
  email: string;
  phone: string;
  location: string;
  linkedIn?: string;

  // Startup Info
  startupName: string;
  website?: string;
  industry: string;
  stage: string;

  // Idea Details
  problemStatement: string;
  solution: string;
  targetMarket: string;
  uniqueValue: string;

  // Traction & Validation
  traction?: string;
  revenue?: string;
  teamSize: string;
  cofounderBackground?: string;

  // Grant Preferences
  preferredTier: 'standard' | 'strong';
  whyGrant: string;
  commitment: string;

  // Meta
  createdAt: Timestamp;
  status: 'pending' | 'reviewing' | 'approved' | 'rejected';
  source: string;
}

// Submit grant application to Firestore
export const submitGrantApplication = async (
  application: Omit<GrantApplication, 'createdAt' | 'status'>
): Promise<{ success: boolean; id?: string; error?: string }> => {
  try {
    const docRef = await addDoc(collection(db, 'grantApplications'), {
      ...application,
      createdAt: Timestamp.now(),
      status: 'pending',
    });

    // Track the submission
    trackEvent('grant_application_submit', {
      tier: application.preferredTier,
      industry: application.industry,
      stage: application.stage,
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error submitting grant application:', error);
    return { success: false, error: (error as Error).message };
  }
};

// Contact form (KODE site)
export interface ContactBrief {
  fullName: string;
  organisation: string;
  role: string;
  productOfInterest: string | null;
  problem: string;
  email: string;
  phone: string | null;
  lang: string;
}

export const submitContactBrief = async (
  brief: ContactBrief
): Promise<{ success: boolean; id?: string; error?: string }> => {
  try {
    const docRef = await addDoc(collection(db, 'contactBriefs'), {
      ...brief,
      source: 'kode-site-contact-form',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      referrer: typeof document !== 'undefined' ? document.referrer : null,
      pageUrl: typeof window !== 'undefined' ? window.location.href : null,
      status: 'new',
      createdAt: Timestamp.now(),
    });

    trackEvent('contact_brief_submit', {
      lang: brief.lang,
      product: brief.productOfInterest || 'unspecified',
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error submitting contact brief:', error);
    return { success: false, error: (error as Error).message };
  }
};

export { app, analytics, db };
